import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Download, Wrench, Scale, FileSignature } from "lucide-react";
import { toast } from "sonner";

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

const TIPO = {
  zavorre: { label: "Zavorre (tetto piano)", Icon: Scale, cls: "border-amber-300 bg-amber-100 text-amber-800" },
  fissaggi_piano: { label: "Fissaggi tetto piano", Icon: Wrench, cls: "border-blue-300 bg-blue-100 text-blue-800" },
  fissaggi_inclinato: { label: "Fissaggi tetto inclinato", Icon: Wrench, cls: "border-indigo-300 bg-indigo-100 text-indigo-800" },
};

const riepilogo = (c) => {
  const s = c.summary || {};
  if (c.tipo === "zavorre") {
    const p = s.peso_totale_kg != null ? `${Math.round(s.peso_totale_kg)} kg zavorra` : null;
    return [s.n_prsg != null ? `${s.n_prsg} PRSG` : null, p].filter(Boolean).join(" · ") || "—";
  }
  const t = s.totali || {};
  return [s.n_pannelli != null ? `${s.n_pannelli} pann.` : null,
    t.barre_intere != null ? `${t.barre_intere} barre` : null,
    t.staffe != null ? `${t.staffe} staffe` : null].filter(Boolean).join(" · ") || "—";
};

export default function ConsulenzeTecnicheAnagrafica({ installerId, testid = "consulenze-tecniche-anag" }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = () => {
    if (!installerId) return;
    setLoading(true);
    api.get(`/consulenze-tecniche?installer_id=${installerId}`)
      .then((r) => setRows(r.data?.consulenze || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [installerId]);

  const downloadPdf = async (c) => {
    try {
      const r = await api.get(`/consulenze-tecniche/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = c.pdf_filename || "consulenza-tecnica.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Impossibile scaricare il PDF"); }
  };

  return (
    <Card className="p-6" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-lg font-bold tracking-tight">Consulenze tecniche archiviate</h2>
        {!loading && <Badge variant="secondary" className="ml-1" data-testid={`${testid}-count`}>{rows.length}</Badge>}
      </div>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>Nessuna consulenza tecnica salvata per questa anagrafica.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Cliente finale</TableHead>
                <TableHead>Riepilogo</TableHead>
                <TableHead className="text-right">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => {
                const t = TIPO[c.tipo] || { label: c.tipo, Icon: Wrench, cls: "" };
                const Icon = t.Icon;
                return (
                  <TableRow key={c.id} data-testid={`${testid}-row-${c.id}`}>
                    <TableCell className="text-sm text-muted-foreground">{itDate(c.created_at)}</TableCell>
                    <TableCell><Badge className={`gap-1 ${t.cls}`}><Icon className="h-3 w-3" /> {t.label}</Badge></TableCell>
                    <TableCell className="text-sm font-semibold">{c.cliente || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{riepilogo(c)}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => downloadPdf(c)} data-testid={`${testid}-pdf-${c.id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                        <Download className="h-4 w-4" /> Scarica
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
